// Libs
import React, { useState, useCallback, useEffect, useRef } from 'react'
import moment from 'moment'

import useInterval from 'core/hooks/useInterval'
import Button from 'core/elements/button'

export enum POLLING_INTERVALS {
  FIFTEEN_SECONDS = 1000 * 15,
  THIRTY_SECONDS = 1000 * 30,
  ONE_MINUTE = 1000 * 60,
  TWO_MINUTES = 1000 * 60 * 2,
  FIVE_MINUTES = 1000 * 60 * 5,
}

interface Props {
  loading: boolean
  hidden?: boolean
  pause?: boolean
  pollIntervalMs?: number
  refreshDuration?: number
  onReload: (ignoreCache?: boolean, updateLoadingState?: boolean) => Promise<void>
  pollingCondition?: () => boolean
}

const defaultRefreshDuration = 1000 * 60 * 5

function PollingData({
  loading: parentLoading,
  onReload,
  hidden = false,
  pause = false,
  pollIntervalMs = 5000,
  refreshDuration = defaultRefreshDuration,
  pollingCondition,
}: Props) {
  const [lastIntervalTs, setLastIntervalTs] = useState(Date.now())
  const [lastFetchTs, setLastFetchTs] = useState(Date.now())
  const reload = useCallback(() => {
    const ts = Date.now()
    setLastFetchTs(ts)
    setLastIntervalTs(ts)
    if (typeof pollingCondition === 'function' && !pollingCondition()) {
      return
    }
    onReload(true, false)
  }, [setLastFetchTs, setLastIntervalTs, onReload, pollingCondition])

  const loadingRef = useRef(parentLoading)
  useEffect(() => {
    loadingRef.current = parentLoading
  }, [parentLoading])

  const intervalRef = useRef<number | NodeJS.Timeout | null>(null)

  // Handle interval updates - always call the hook but conditionally execute logic
  useInterval(() => {
    if (!pause && !loadingRef.current) {
      setLastIntervalTs(Date.now())
    }
  }, pollIntervalMs)

  // Function to check if we need to reload based on refresh duration
  const checkRefreshNeeded = useCallback(() => {
    // Don't perform refresh check if paused
    if (pause) {
      return
    }

    const currentTs = Date.now()
    if (currentTs - lastFetchTs > refreshDuration) {
      reload()
    }
  }, [pause, lastFetchTs, refreshDuration, reload])

  // Handle refresh duration check - separate from the interval
  useEffect(() => {
    // Initial check
    checkRefreshNeeded()

    // Set up periodic checks
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }
    intervalRef.current = setInterval(checkRefreshNeeded, pollIntervalMs)

    // Clean up
    return () => {
      intervalRef.current && clearInterval(intervalRef.current)
    }
  }, [pollIntervalMs, checkRefreshNeeded])

  if (hidden) {
    return null
  }

  return (
    <Button variant="tertiary" onClick={pause ? undefined : reload} icon="sync">
      {parentLoading ? 'loading...' : moment(lastIntervalTs).fromNow()}
    </Button>
  )
}

export default PollingData
